import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  prcVersionArg,
  useGetPrcVersionQuery,
  useSetPrcSelectionMutation,
  type PrcModelRow,
  type PrcVersionPayload,
} from '@shared/api';
import { IconSearch } from '@shared/ui/icons';
import { Input } from '@shared/ui/Input';
import {
  FleetChip,
  NBSP,
  ScrollRegion,
  VisuallyHidden,
  fleetLeading,
  fleetType,
  formatCount,
  nameKey,
  typo,
} from '@entities/fleet';
import { PRC_COST_ARTICLES, isEditableStatus } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { MODELS, count, formatMoneyCell, formatPercent, formatRub } from '../format';
import { Chips, NARROW, NumTable, SegmentButton, Segmented, Toolbar, YearSwitch } from '../ui';
import { ModelDrawer } from './ModelDrawer';

type Filter = 'all' | 'errors' | 'warnings' | 'limits' | 'unselected';

const Search = styled.div`
  position: relative;
  flex: 1 1 240px;
  min-width: 0;
  max-width: 360px;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.secondary};
    pointer-events: none;
  }

  @container prc (max-width: ${NARROW}) {
    max-width: none;
    flex-basis: 100%;
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing(9)};
`;

const Selection = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Units = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TableBox = styled.div<{ $stale: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  opacity: ${({ $stale }) => ($stale ? 0.55 : 1)};
  transition: opacity ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const Table = styled(NumTable)`
  td.c-name {
    min-width: 240px;
    max-width: 340px;
  }

  td.c-check {
    width: 40px;
    padding-right: 0;
  }

  th.price,
  td.price {
    background: ${({ theme }) => theme.colors.surface};
  }

  .narrow-only {
    display: none;
  }

  @container prc (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & th,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      column-gap: ${({ theme }) => theme.spacing(3)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      min-width: 0;
      max-width: none;
      background: none;
    }

    td.c-check {
      grid-column: 1;
      grid-row: 1 / span 2;
      width: auto;
    }

    td.c-name {
      grid-column: 2;
    }

    td.c-figures {
      grid-column: 2;
    }

    tr.group > th {
      padding: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(2)}`};
    }
  }
`;

const NameButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  text-align: left;
  text-wrap: pretty;
  overflow-wrap: anywhere;
  cursor: pointer;

  tr.row:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const RowError = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.status.danger};
  overflow-wrap: anywhere;
`;

const RowChips = styled(Chips)`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

/** Метка «ремонт по перечню лимитов» у суммы ремонта — точкой, как в легенде. */
const LimitsMark = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: ${({ theme }) => theme.spacing(1.5)};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.info};
  vertical-align: 2px;
`;

const LegendDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.info};
  }
`;

const Check = styled.input`
  width: 18px;
  height: 18px;
  margin: 1px 0 0;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const Figures = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};

  & dt {
    font-size: ${fleetType.micro};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: 0;
    font-size: ${fleetType.body};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

interface Group {
  key: string;
  title: string;
  models: PrcModelRow[];
}

function groupRows(models: readonly PrcModelRow[]): Group[] {
  const out: Group[] = [];
  for (const m of models) {
    const title = m.category_name ?? 'Без категории';
    const last = out[out.length - 1];
    if (last && last.title === title) last.models.push(m);
    else out.push({ key: `${title}-${out.length}`, title, models: [m] });
  }
  return out;
}

function matches(m: PrcModelRow, filter: Filter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'errors':
      return m.error !== null;
    case 'warnings':
      return m.warnings.length > 0;
    case 'limits':
      return m.repair_limits !== null;
    case 'unselected':
      return !m.selected;
  }
}

/** Цена — сразу за названием: её смотрят первой; статьи — следом, в порядке листа. */
const NUM_COLUMNS = [
  { code: 'price' as const, label: 'Цена без НДС', price: true },
  { code: 'price_with_vat' as const, label: 'Цена с НДС', price: true },
  { code: 'cost' as const, label: 'Себестоимость', price: false },
  ...PRC_COST_ARTICLES.map((a) => ({ code: a.code, label: a.short, price: false })),
];

/**
 * Вкладка «Результаты»: модели по категориям, девять статей, себестоимость,
 * цена без и с НДС за выбранный год; отметки «утверждать»; ошибка и
 * предупреждения модели; клик по модели — полный расчёт по годам.
 */
export function ResultsTab({
  payload,
  access,
  year,
  onYear,
  openModelId,
  onOpenModel,
  onCloseModel,
  onBreakEven,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  year: number;
  onYear: (year: number) => void;
  openModelId: string | null;
  onOpenModel: (id: string) => void;
  onCloseModel: () => void;
  onBreakEven: (modelId: string) => void;
}) {
  const v = payload.version;
  const baseYear = v.params.base_year;
  const yearArg = year === baseYear ? undefined : year;
  const variantId = payload.variant?.id;
  const yearQuery = useGetPrcVersionQuery(prcVersionArg(v.id, yearArg, variantId), {
    skip: yearArg === undefined,
  });
  const view = yearArg === undefined ? payload : (yearQuery.data ?? payload);
  const stale =
    yearArg !== undefined && (yearQuery.isFetching || yearQuery.currentData === undefined);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [select, selectState] = useSetPrcSelectionMutation();
  const [selectError, setSelectError] = useState<string | null>(null);
  const canSelect = access.canWrite && isEditableStatus(v.status);

  const models = view.models;
  const counts = useMemo(
    () => ({
      errors: models.filter((m) => m.error !== null).length,
      warnings: models.filter((m) => m.warnings.length > 0).length,
      limits: models.filter((m) => m.repair_limits !== null).length,
      unselected: models.filter((m) => !m.selected).length,
    }),
    [models],
  );
  const visible = useMemo(() => {
    const q = nameKey(search);
    return models.filter((m) => matches(m, filter) && (!q || nameKey(m.model_name).includes(q)));
  }, [models, filter, search]);
  const groups = useMemo(() => groupRows(visible), [visible]);
  const order = useMemo(() => visible.map((m) => m.model_id), [visible]);
  const selected = models.filter((m) => m.selected).length;

  async function toggle(ids: string[], value: boolean) {
    setSelectError(null);
    try {
      await select({
        id: v.id,
        model_ids: ids,
        selected: value,
        year: yearArg,
        variant: variantId,
      }).unwrap();
    } catch (err) {
      setSelectError(apiErrorText(err, 'Не удалось изменить отметки «утверждать».'));
    }
  }

  const filters: { id: Filter; label: string; n: number }[] = [
    { id: 'errors', label: 'С ошибкой', n: counts.errors },
    { id: 'warnings', label: 'С предупреждениями', n: counts.warnings },
    { id: 'limits', label: 'Ремонт по перечню', n: counts.limits },
    { id: 'unselected', label: 'Не утверждаются', n: counts.unselected },
  ];

  return (
    <>
      <Toolbar>
        <YearSwitch years={v.params.years} baseYear={baseYear} value={year} onChange={onYear} />
        <Search role="search">
          <IconSearch size={16} />
          <SearchInput
            type="search"
            value={search}
            placeholder="Модель"
            aria-label="Поиск модели по названию"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Search>
        <Segmented role="group" aria-label="Показать модели">
          <SegmentButton
            type="button"
            aria-pressed={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            Все · {formatCount(models.length)}
          </SegmentButton>
          {filters
            .filter((f) => f.n > 0)
            .map((f) => (
              <SegmentButton
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(filter === f.id ? 'all' : f.id)}
              >
                {f.label} · {formatCount(f.n)}
              </SegmentButton>
            ))}
        </Segmented>
      </Toolbar>

      <Selection>
        <span>
          {canSelect ? 'К утверждению' : 'Отмечено к утверждению'}{' '}
          <strong>
            {formatCount(selected)} из {formatCount(models.length)}
          </strong>
          {canSelect &&
            counts.errors > 0 &&
            typo(` — модели с ошибкой расчёта не утвердятся, даже если отмечены`)}
        </span>
        {canSelect && (
          <>
            <TextButton
              type="button"
              disabled={selectState.isLoading || selected === models.length}
              onClick={() => void toggle([], true)}
            >
              Выделить все
            </TextButton>
            <TextButton
              type="button"
              disabled={selectState.isLoading || selected === 0}
              onClick={() => void toggle([], false)}
            >
              Снять все
            </TextButton>
          </>
        )}
      </Selection>

      {selectError && (
        <ErrorBox>
          <Notice $tone="danger" role="alert">
            <span className="text">{selectError}</span>
          </Notice>
        </ErrorBox>
      )}

      <Units>
        {typo(`Суммы — ₽${NBSP}на${NBSP}маш.-час; статьи, себестоимость и цена — без НДС.`)}{' '}
        {counts.limits > 0 && (
          <LegendDot>{typo('ремонт и ТО по перечню лимитов на ремонт')}</LegendDot>
        )}
      </Units>

      <TableBox $stale={stale} aria-busy={stale}>
        {stale && <VisuallyHidden role="status">Загружаю цифры {year} года</VisuallyHidden>}
        {visible.length === 0 ? (
          <Notice $tone="info">
            <span className="text">Ничего не нашлось.</span>
            <TextButton
              type="button"
              onClick={() => {
                setSearch('');
                setFilter('all');
              }}
            >
              Сбросить поиск
            </TextButton>
          </Notice>
        ) : (
          <ScrollRegion label={`Результаты расчёта ${year} года`}>
            <Table>
              <caption>
                <VisuallyHidden>
                  ПРЦ моделей за {year} год, ₽ на маш.-час без НДС, если не сказано иное
                </VisuallyHidden>
              </caption>
              <thead>
                <tr>
                  {canSelect && (
                    <th scope="col" className="c-check">
                      <span aria-hidden="true">Утв.</span>
                      <VisuallyHidden>Утверждать</VisuallyHidden>
                    </th>
                  )}
                  <th scope="col" className="sticky">
                    Модель · {year}
                  </th>
                  {NUM_COLUMNS.map((c) => (
                    <th key={c.code} scope="col" className={c.price ? 'num price' : 'num'}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              {groups.map((g) => (
                <tbody key={g.key}>
                  <tr className="group">
                    <th colSpan={NUM_COLUMNS.length + (canSelect ? 2 : 1)} scope="rowgroup">
                      {g.title}
                      <span className="meta">{count(g.models.length, MODELS)}</span>
                    </th>
                  </tr>
                  {g.models.map((m) => (
                    <ResultRow
                      key={m.model_id}
                      model={m}
                      canSelect={canSelect}
                      busy={selectState.isLoading}
                      onToggle={(value) => void toggle([m.model_id], value)}
                      onOpen={() => onOpenModel(m.model_id)}
                    />
                  ))}
                </tbody>
              ))}
            </Table>
          </ScrollRegion>
        )}
      </TableBox>

      {openModelId && (
        <ModelDrawer
          versionId={v.id}
          version={v}
          conditions={variantId}
          modelId={openModelId}
          preview={models.find((m) => m.model_id === openModelId)}
          year={year}
          siblings={order}
          onSwitch={onOpenModel}
          onClose={onCloseModel}
          onBreakEven={onBreakEven}
        />
      )}
    </>
  );
}

function ResultRow({
  model: m,
  canSelect,
  busy,
  onToggle,
  onOpen,
}: {
  model: PrcModelRow;
  canSelect: boolean;
  busy: boolean;
  onToggle: (value: boolean) => void;
  onOpen: () => void;
}) {
  const limits = m.repair_limits;
  return (
    <tr
      className="row"
      onClick={(e) => {
        if (window.getSelection()?.toString()) return;
        if (e.target instanceof HTMLInputElement) return;
        onOpen();
      }}
    >
      {canSelect && (
        <td className="c-check">
          <Check
            type="checkbox"
            checked={m.selected}
            disabled={busy}
            aria-label={`Утверждать «${m.model_name}»`}
            onChange={(e) => onToggle(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </td>
      )}
      <td className="c-name sticky">
        <NameButton
          type="button"
          data-prc-open={m.model_id}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {m.model_name}
        </NameButton>
        {m.error && <RowError>{typo(m.error)}</RowError>}
        <RowChips>
          {m.warnings.length > 0 && (
            <FleetChip $tone="warning" title={m.warnings.join('\n')}>
              Предупреждений: {m.warnings.length}
            </FleetChip>
          )}
          {!canSelect && !m.selected && <FleetChip $tone="muted">не утверждается</FleetChip>}
        </RowChips>
      </td>
      {NUM_COLUMNS.map((c) => (
        <td
          key={c.code}
          className={`wide-only num${c.price ? ' price' : ''}${c.code === 'price' ? ' strong' : ''}`}
        >
          {c.code === 'repair' && limits && (
            <LimitsMark
              title={`Норма ремонта по перечню лимитов ${formatPercent(limits.norm_pct, 2)}, в паспорте ${formatPercent(limits.passport_norm_pct, 2)}`}
            >
              <VisuallyHidden>по перечню лимитов: </VisuallyHidden>
            </LimitsMark>
          )}
          {formatMoneyCell(m.figures[c.code])}
        </td>
      ))}
      <td className="narrow-only c-figures">
        {!m.error && (
          <Figures>
            <div>
              <dt>Цена без НДС</dt>
              <dd>{formatRub(m.figures.price)}</dd>
            </div>
            <div>
              <dt>С НДС</dt>
              <dd>{formatRub(m.figures.price_with_vat)}</dd>
            </div>
            <div>
              <dt>Себестоимость</dt>
              <dd>{formatRub(m.figures.cost)}</dd>
            </div>
          </Figures>
        )}
      </td>
    </tr>
  );
}
